import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { PopularLazyLoadSelector } from 'webapp/utils/common';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';
import { cleanupDuplicateNodes } from 'webapp/utils/html';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import CommonRecentlyDeletedInfoComponent from 'webapp/components/common/recently-deleted-info';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { Args as ReportsIndexBaseArgs } from 'webapp/components/reports/index/base';
import ReportsIndexBaseComponent from 'webapp/components/reports/index/base';
import ReportsIndexTableRowComponent from 'webapp/components/reports/index/table-row';
import SkeletonScreenComponent from 'webapp/components/skeleton-screen';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';
import TemplatesTransferedNotificationAccountTableRowComponent from 'webapp/components/templates/transfered-notification/account/table-row';
import TemplatesTransferedNotificationPublicReportTableRowComponent from 'webapp/components/templates/transfered-notification/public/report-table-row';

import FilterSortReportInList from 'webapp/models-custom/filter-sort/report-in-list';

export default class ReportsIndexTableComponent extends ReportsIndexBaseComponent {
	@service('account') declare accountService: Service.Account;

	override filterSort: FilterSortReportInList;

	constructor(owner: Owner, args: ReportsIndexBaseArgs) {
		super(owner, args);

		const filterSort = new FilterSortReportInList({
			selectors: PopularLazyLoadSelector.table,
			getAllItems: () => {
				return this.model;
			},
			onOpenView: (viewId: string) => {
				this.args.openView(viewId);
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}

				this.args.updateIsRecentlyDeletedFilterActive(filterSort.isRecentlyDeletedSelected);
			},
		});

		this.filterSort = filterSort;
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'tr.table-row[data-id]',
			shouldKeepInSection: (itemId, sectionSelector) => {
				const report = this.model.find((r) => r.id === itemId);
				if (!report) {
					return false;
				}
				return (
					(report.favorite && sectionSelector === '.favorites-section') ||
					(!report.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}

	@action
	setupView(node: HTMLElement, sortingFavorites: boolean): void {
		this.visibleCheckContainerNode = node;

		if (!this.accountService.isDemoAccount) {
			const ctEl = $(node).find('tbody'),
				itemSelectorString = this.filterSort.itemDomSelector,
				placeholderElementFn = function (currentItem: JQuery) {
					const el = $('<tr />');
					el.addClass('drop-placeholder').append(
						`<td colspan="${currentItem.find('>td:not(.toggle)').length}"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></td>`,
					);
					return el;
				};
			this.initSortable(ctEl, itemSelectorString, placeholderElementFn, sortingFavorites);
		}
	}

	@action
	setupFavoritesView(node: HTMLElement): void {
		this.setupView(node, true);
	}

	@action
	setupNonFavoritesView(node: HTMLElement): void {
		this.setupView(node, false);
	}

	@action
	teardownView(): void {
		this.destroySortable();
	}

	@action
	cleanupDuplicateNodes(): void {
		cleanupDuplicateNodes(this.duplicateCleanupConfig);
	}
	<template>
		<div class="sortable-table-container">
			<FilterLineProxyComponent @model={{this.filterSort}} />

			<FilterNoResultsComponent @model={{this.filterSort}} @entityName="reports" />

			{{#if this.isRecentlyDeletedActiveWithResults}}
				<CommonRecentlyDeletedInfoComponent
					@onDeleteAllPermanently={{this.deleteAllPermanently}}
				/>
			{{/if}}

			{{#if this.isRecentlyDeletedLoading}}
				<SkeletonScreenComponent @type="table" />
			{{/if}}

			{{! Notifications reports section }}
			<table
				class={{joinClasses
					(array
						"flexible-table"
						"bigtable"
						"has-checkbox"
						"has-avatar-big"
						"notifications-section"
					)
				}}
			>
				<colgroup>
					<col class="cc" />
					<col class="h0" />
					<col width="25%" />
					<col width="13%" />
					<col width="15%" />
					<col width="15%" />
					<col class="button-ct" />
				</colgroup>

				<thead class={{unless this.isNotificationsSectionVisible "hidden"}}>
					<tr>
						{{#if this.isNotificationsSectionVisible}}
							<CommonBatchTableHeaderComponent @batchSelect={{this.batchSelect}} />
						{{else}}
							<th class="cc"></th>
						{{/if}}
						<th class="h0">Name</th>
						<th></th>
						<th class="prio-low">Last edited</th>
						<th class="prio-medium">Owner</th>
						<th class="prio-low">Tags</th>
						<th class="button-ct"></th>
					</tr>
				</thead>

				<tbody>
					{{#unless this.isRecentlyDeletedActive}}
						{{#each @transferedReportAccountTemplateNotifications as |notification|}}
							<TemplatesTransferedNotificationAccountTableRowComponent
								@model={{notification}}
								@cellsInRowCount={{7}}
								@mediumPriorityCellsInRowCount={{1}}
								@lowPriorityCellsInRowCount={{2}}
								@extraClasses={{array "notification--account"}}
							/>
						{{/each}}

						{{#each @transferedReportPublicTemplateNotifications as |notification|}}
							<TemplatesTransferedNotificationPublicReportTableRowComponent
								@model={{notification}}
							/>
						{{/each}}
					{{/unless}}
				</tbody>
			</table>

			{{! Favorite reports section }}
			<CommonListLazyLoadComponent
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.favoriteReportsFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>
				<table
					class={{joinClasses
						(array
							"flexible-table"
							"bigtable"
							"has-checkbox"
							"has-avatar-big"
							"favorites-section"
							(if this.filterSort.noResults "hide" "")
						)
					}}
					{{didInsert this.setupFavoritesView}}
					{{visibleCheckContainer}}
					{{didUpdate this.triggerContentChange visibleItems}}
					{{willDestroy this.teardownView}}
				>
					<colgroup>
						<col class="cc" />
						<col class="h0" />
						<col width="25%" />
						<col width="13%" />
						<col width="15%" />
						<col width="15%" />
						<col class="button-ct" />
					</colgroup>

					<thead class={{unless this.isFavoriteSectionVisible "hidden"}}>
						<tr>
							{{#if this.isFavoriteSectionVisible}}
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
							{{else}}
								<th class="cc"></th>
							{{/if}}
							<th class="h0">Name</th>
							<th></th>
							<th class="prio-low">Last edited</th>
							<th class="prio-medium">Owner</th>
							<th class="prio-low">Tags</th>
							<th class="button-ct"></th>
						</tr>
					</thead>

					<tbody>
						{{#each visibleItems as |item|}}
							<ReportsIndexTableRowComponent
								@model={{item}}
								@batchSelect={{this.batchSelect}}
								@hideSort={{exporter.hideSort}}
								@trackingOrigin={{this.trackingOrigin}}
							/>
						{{/each}}
					</tbody>
				</table>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>

			{{! Non-favorite reports section }}
			<CommonListLazyLoadComponent
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.nonFavoriteReportsFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>
				<table
					class={{joinClasses
						(array
							"flexible-table"
							"bigtable"
							"has-checkbox"
							"has-avatar-big"
							"non-favorites-section"
							(if this.filterSort.noResults "hide" "")
						)
					}}
					{{didInsert this.setupNonFavoritesView}}
					{{visibleCheckContainer}}
					{{didUpdate this.triggerContentChange visibleItems}}
				>
					<colgroup>
						<col class="cc" />
						<col class="h0" />
						<col width="25%" />
						<col width="13%" />
						<col width="15%" />
						<col width="15%" />
						<col class="button-ct" />
					</colgroup>

					<thead class={{unless this.isNonFavoriteSectionVisible "hidden"}}>
						<tr>
							{{#if this.isNonFavoriteSectionVisible}}
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
							{{else}}
								<th class="cc"></th>
							{{/if}}
							<th class="h0">Name</th>
							<th></th>
							<th class="prio-low">Last edited</th>
							<th class="prio-medium">Owner</th>
							<th class="prio-low">Tags</th>
							<th class="button-ct"></th>
						</tr>
					</thead>

					<tbody>
						{{#each visibleItems as |item|}}
							<ReportsIndexTableRowComponent
								@model={{item}}
								@batchSelect={{this.batchSelect}}
								@hideSort={{exporter.hideSort}}
								@trackingOrigin={{this.trackingOrigin}}
								@onPermanentDelete={{this.handleSingleItemPermanentDelete}}
								@onRestore={{this.handleSingleItemRestore}}
							/>
						{{/each}}
					</tbody>
				</table>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>

		</div>

		{{#if this.addBtnShown}}
			<TableAddNewItemComponent
				@title="New report"
				@addNewItem={{fn @onReportCreateChoice "listing"}}
				@model={{this.filterSort}}
			/>
		{{/if}}

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent
					@text={{this.addButtonText}}
					@callTrigger={{fn @onReportCreateChoice "reports_new"}}
					@options={{this.addButtonOptions}}
					@onOptionClick={{this.doAddButtonAction}}
				/>
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
